/**
 * The Team settings sheet's small parts, in the prototype's classes
 * (`styles/queue.css`: `.field`, `.seg`, `.tags`, `.agentset`, `.acts`): one
 * labelled row per setting, a segmented choice, a list of chips a person adds
 * to and removes from, and the Agent card each section is.
 */
import { useId, useState, type CSSProperties, type ReactNode } from "react";
import type { WorkerAvatar } from "@yagni/shared";

import { cx } from "../../queue/index.js";
import { WorkerMark } from "../../workers/WorkerMark.js";

export function Field({
  label,
  top,
  help,
  htmlFor,
  children,
  testId,
}: {
  label: ReactNode;
  top?: boolean;
  help?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div className={cx("field", top && "top")} data-testid={testId}>
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {help != null && help !== false && <div className="help">{help}</div>}
    </div>
  );
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  help,
  disabled,
  mono,
  testId,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  help?: ReactNode;
  disabled?: boolean;
  mono?: boolean;
  testId?: string;
  maxLength?: number;
}) {
  const id = useId();
  return (
    <Field label={label} help={help} htmlFor={id}>
      <div className={cx("in", mono && "mono")}>
        <input
          id={id}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          data-testid={testId}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
    </Field>
  );
}

/** A whole number in a range. An empty box means the line's default. */
export function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  placeholder,
  help,
  disabled,
  testId,
}: {
  label: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  min: number;
  max: number;
  placeholder?: string;
  help?: ReactNode;
  disabled?: boolean;
  testId?: string;
}) {
  const id = useId();
  const [text, setText] = useState(value === undefined ? "" : String(value));
  const parsed = text.trim() === "" ? undefined : Number(text);
  const invalid = parsed !== undefined && (!Number.isInteger(parsed) || parsed < min || parsed > max);
  return (
    <Field
      label={label}
      htmlFor={id}
      help={
        invalid ? (
          <span style={{ color: "var(--incident)" }}>
            A whole number from {min} to {max}.
          </span>
        ) : (
          help
        )
      }
    >
      <div className="in" style={{ maxWidth: 120 }}>
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={text}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          data-testid={testId}
          onChange={(event) => {
            const next = event.target.value;
            setText(next);
            const number = next.trim() === "" ? undefined : Number(next);
            if (number === undefined) onChange(undefined);
            else if (Number.isInteger(number) && number >= min && number <= max) onChange(number);
          }}
        />
      </div>
    </Field>
  );
}

export function AreaField({
  label,
  value,
  onChange,
  placeholder,
  help,
  disabled,
  rows = 3,
  testId,
  maxLength = 4000,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  help?: ReactNode;
  disabled?: boolean;
  rows?: number;
  testId?: string;
  maxLength?: number;
}) {
  const id = useId();
  return (
    <Field label={label} top help={help} htmlFor={id}>
      <textarea
        id={id}
        rows={rows}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        data-testid={testId}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  help,
  disabled,
  testId,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  help?: ReactNode;
  disabled?: boolean;
  testId?: string;
}) {
  const id = useId();
  return (
    <Field label={label} help={help} htmlFor={id}>
      <select id={id} value={value} disabled={disabled} data-testid={testId} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

/** A segmented choice: one of a few, each a button. */
export function Seg<T extends string>({
  options,
  value,
  onChange,
  disabled,
  label,
  className,
  testId,
}: {
  options: ReadonlyArray<{ value: T; label: ReactNode }>;
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  label: string;
  className?: string;
  testId?: string;
}) {
  return (
    <span className={cx("seg", className)} role="radiogroup" aria-label={label} data-testid={testId}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          className={cx(option.value === value && "on")}
          disabled={disabled}
          data-value={option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </span>
  );
}

/** Chips a person adds to and removes from: paths, authors, repositories. */
export function ChipList({
  label,
  items,
  onChange,
  addLabel,
  placeholder,
  help,
  disabled,
  testId,
}: {
  label: string;
  items: readonly string[];
  onChange: (items: string[]) => void;
  addLabel: string;
  placeholder?: string;
  help?: ReactNode;
  disabled?: boolean;
  testId?: string;
}) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const add = (value: string) => {
    const next = value.trim();
    if (next && !items.includes(next)) onChange([...items, next]);
    setText("");
  };
  return (
    <Field label={label} top help={help} testId={testId}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
        <div className="tags">
          {items.map((item) => (
            <span key={item} className="chip entry">
              {item}
              {!disabled && (
                <button type="button" className="x" aria-label={`Remove ${item}`} onClick={() => onChange(items.filter((entry) => entry !== item))}>
                  ×
                </button>
              )}
            </span>
          ))}
          {!disabled && !adding && (
            <button type="button" className="chip add" onClick={() => setAdding(true)}>
              + {addLabel}
            </button>
          )}
        </div>
        {adding && (
          <div className="inline">
            <span className="in mono">
              <input
                autoFocus
                value={text}
                placeholder={placeholder}
                aria-label={addLabel}
                onChange={(event) => setText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    add(text);
                  } else if (event.key === "Escape") {
                    event.preventDefault();
                    event.stopPropagation();
                    setAdding(false);
                    setText("");
                  }
                }}
              />
            </span>
            <button type="button" className="btn secondary sm" onClick={() => add(text)} disabled={!text.trim()}>
              Add
            </button>
            <button
              type="button"
              className="btn quiet sm"
              onClick={() => {
                setAdding(false);
                setText("");
              }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </Field>
  );
}

/** An Agent's mark at a given pixel size. */
export function Mark({ avatar, name, slug, size }: { avatar: WorkerAvatar; name: string; slug?: string; size: number }) {
  return (
    <span className="setmark" style={{ width: size, height: size }} aria-hidden>
      <WorkerMark avatar={avatar} name={name} slug={slug} size="sm" />
    </span>
  );
}

/** One section of the sheet: the Agent's mark, name and role, then its rows. */
export function AgentCard({
  id,
  mark,
  name,
  role,
  sub,
  headRight,
  children,
  color,
}: {
  id: string;
  mark: ReactNode;
  name: ReactNode;
  role: ReactNode;
  sub?: ReactNode;
  headRight?: ReactNode;
  children: ReactNode;
  color?: string;
}) {
  return (
    <section
      className="agentset"
      id={`set-${id}`}
      data-section={id}
      data-testid={`settings-${id}`}
      style={color ? ({ "--agent": color } as CSSProperties) : undefined}
    >
      <div className="ah">
        {mark}
        <span style={{ minWidth: 0, flex: 1 }}>
          <div className="nm">{name}</div>
          <div className="role">{role}</div>
          {sub && <div className="sub">{sub}</div>}
        </span>
        {headRight}
      </div>
      {children}
    </section>
  );
}

export function Sect({ children }: { children: ReactNode }) {
  return <div className="sect">{children}</div>;
}

/** The head-right fact an Agent card carries when its rung is not a choice. */
export function HeadFact({ children }: { children: ReactNode }) {
  return (
    <span className="fact" style={{ fontSize: 11, marginLeft: "auto", textAlign: "right", maxWidth: 260 }}>
      {children}
    </span>
  );
}
